import { FC, useMemo, useEffect } from 'react';
import { useStore } from 'hooks/useRootStore';
import { observer } from 'mobx-react-lite';
import { useMutation } from 'react-query';
import { useNavigate, useParams } from 'react-router';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { BrandsRequestsTable } from 'components/Tables/BrandsRequestsTable/BrandsRequestsTable';
import { PointsRequestsTable } from 'components/Tables/PointsRequestsTable/PointsRequestsTable';
import { ProductsRequestsTable } from 'components/Tables/ProductsRequestsTable/ProductsRequestsTable';
import { AppLoader } from 'components/Core/AppLoader';
import { requestsTranslations } from './translations';
import { RequestsTransFields } from './types';
import { tabs } from './constatnts';
import styles from './requests.module.scss';
import { EntityType } from 'types/common';
import { CataloguesRequestsTable } from 'components/Tables/CataloguesRequestsTable/CataloguesRequestsTable';

const Requests: FC = observer(() => {
  const {
    requestsStore,
    requestsStore: { getRequestsData, getRequestsCount },
    mainStore: { user },
  } = useStore();

  const { t } = useMyTranslations<RequestsTransFields>([requestsTranslations.name]);

  const { entity } = useParams();
  const navigate = useNavigate();

  const { mutate: getCounts } = useMutation(getRequestsCount, {
    mutationKey: 'getRequestsCount',
    retry: false,
  });

  const { mutate, isLoading } = useMutation(getRequestsData, {
    mutationKey: 'getRequestsData',
    retry: false,
  });

  useEffect(() => {
    mutate(entity as EntityType);
    getCounts();
  }, [entity, user?.selectedCountryCode]);

  const onChangeTab = (tab: EntityType) => {
    if (tab !== entity) {
      navigate(`/requests/${tab}`);
    }
  };

  const requestsTable = useMemo(() => {
    switch (entity) {
      case 'brands':
        return <BrandsRequestsTable />;
      case 'points':
        return <PointsRequestsTable />;
      case 'products':
        return <ProductsRequestsTable />;
      case 'catalogues':
        return <CataloguesRequestsTable />;
      default:
        return null;
    }
  }, [entity]);

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <h2 className={styles.headerTitle}>{t('title')}</h2>
      </header>
      <div className={styles.tabs}>
        {tabs.map(
          (item) =>
            !item.hide && (
              <button
                key={item.tab}
                className={`${styles.tab}  ${entity === item.tab && styles.tabCurrent}`}
                type='button'
                onClick={() => onChangeTab(item.tab)}
                disabled={isLoading}>
                <span className={styles.btnTitle}>{item.tab}</span>
                <span className={styles.count}>{requestsStore[item.countKey] || 0}</span>
              </button>
            )
        )}
      </div>
      <div className={styles.container}>{isLoading ? <AppLoader /> : requestsTable}</div>
    </main>
  );
});

export default Requests;
